import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LaunchIcon from '@mui/icons-material/Launch';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import {
  usePostCityLedgerApprovalMutation,
  usePostCityLedgerPaymentBreakdownMutation,
  usePostTransactionEntriesMutation,
} from '../../../../api/front-office/roomposting.api.rtk';
import useDisclosure from '../../../../hooks/useDisclosure';
import { usePopover } from '../../../../hooks/usePopover';
import useToast from '../../../../hooks/useToast';
import useUser from '../../../../hooks/useUser';
import { useRoomPostingContext } from '../../containers/fo-room_posting.container';
import {
  RoomPostingEntryBody,
  RoomPostingTransactionEntry,
} from '../../interface/room-posting-interface';
import { RoomPostingEntrySchema } from '../../interface/room-posting-schema-interface';
import RoomPostingPopover from './fo-roomposting-popover';

import { useAppSelector } from '../../../../store';

import { useCloseCLAccountMutation } from '@api/front-office/cl-accounts.api.rtk';
import { yupResolver } from '@hookform/resolvers/yup';
import useDeclareNewDay from '@hooks/useDeclareNewDay';
import { PrintOutlined, QuestionMarkOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import DOMPurify from 'dompurify';
import moment from 'moment';
import { SubmitHandler, useForm } from 'react-hook-form';
import { usePostCASTransactionMutation } from '../../../../api/cas/cas.api.rtk';
import { useLazyGetPreApprovedQuery } from '../../../../api/front-office/pre-approved.api.rtk';
import AutoCompleteController from '../../../../components/form/controller/autocomplete.controller';
import InputFieldsController from '../../../../components/form/controller/input-fields.controller';
import CustomNumberFormat, {
  CustomNumberFormatProps,
} from '../../../../components/form/custom-number-format';
import TracesListModal from '../../../../components/traces/traces-list-modal';
import CASARVoucher from '../../../../components/voucher/cas-ar-voucher';
import CASSIVoucher from '../../../../components/voucher/cas-si-voucher';
import useAppContext from '../../../../hooks/useAppContext';
import useBookings from '../../../../hooks/useBookings';
import useConfirmationDialog, {
  ConfirmationDialog,
} from '../../../../hooks/useConfirmationDialog';
import useInformationDialog from '../../../../hooks/useInformationDialog';
//import useLocalStorage from '../../../../hooks/useLocalStorage';
import useModal from '../../../../hooks/useModal';
import { CASPostTransaction } from '../../../../interfaces/cas.interface';
import { formatNumber } from '../../../../utils/format';
import FoRoompostingCreditmemoForm from './drawer form/fo-roomposting-creditmemo-form';
import FORoomPostingCreditMemoUsed from './drawer form/fo-roomposting-creditmemo-used-form';
import FORoomPostingTaxCreditForm from './drawer form/fo-roomposting-taxcredit-form';
import FOGuestListTable from './fo-guest-list-table';
import { GuestFolioModal } from './voucher/fo-room-posting-guest-folio';
import { useCurrentModule } from '@hooks/useCurrentModule';
import { selectFOSalesDate } from '@store/selector/app.selector';

const RoomPostingEntry = ({
  onCloseForm = () => false,
  handleVoidItem,
  isNetLocked = false,
  isCityLedgerPosting = false,
  selectedGuestName,
  isSpecificBooking,
  subGroupValue,
  handleCloseResvDrawer,
}: {
  onCloseForm: (forceClose: boolean) => void;
  handleVoidItem?: () => void;
  isNetLocked?: boolean;
  isCityLedgerPosting?: boolean;
  selectedGuestName?: string;
  selectedFolioNo?: string;
  isSpecificBooking?: boolean;
  subGroupValue?: string;
  selectedRoomStatus?: string;
  handleCloseResvDrawer?: () => void;
}) => {
  const currentSalesDate = useAppSelector(selectFOSalesDate);

  const { PopoverWrapper, handleClose, handleToggle, anchorRef, open } =
    usePopover();
  const { toast } = useToast();

  //#region useRoomPostingContext
  const {
    selectedFolioUuid,
    roomPostingLedgerList,
    selectedFolioStatus,
    selectedSubGroup,
    ledgerRefetch,
    transactionType,
    setCLEditedRows,
    clEditedRows,
    isOpen,
  } = useRoomPostingContext();
  //#endregion

  //#region Dialog
  const { DialogInformationV2 } = useInformationDialog();
  const {
    DialogComponentV2: ScheduleDialog,
    DialogComponentV2: PendingDialog,
    DialogComponentV2: TracesDialog,
    DialogComponentV2: PrintFolioDialog,
    DialogComponentV2: PreApprovedDialog,
    DialogComponentV2: ChangeAccountDialog,
    DialogComponentV2: CreditLimitDialog,
    DialogComponentV2: CreditMemoDialog,
  } = useConfirmationDialog();
  //#endregion

  //#region useModal
  const { ModalWrapper, modalDisclosure } = useModal();
  //#endregion

  //#region useAppSelector
  const { selectedProperty } = useAppSelector((state) => ({
    selectedProperty: state.properties.selectedProperty,
  }));

  const moduleSelected = useCurrentModule();

  const entriesList = useAppSelector((state) => state.foEntriesManagement);
  const accountsData = useAppSelector((state) => state.foBookingAccountsList);
  //#endregion

  //#region RTK Query
  const [postCityLedgerApproval] = usePostCityLedgerApprovalMutation();
  const [closeCLAccount] = useCloseCLAccountMutation();
  const [postCASTransactionAPI] = usePostCASTransactionMutation();

  // City Ledger payment breakdown
  const [postCityLedgerPaymentBreakdown] =
    usePostCityLedgerPaymentBreakdownMutation();

  const [getPreApprovedList] = useLazyGetPreApprovedQuery();

  const [
    postTransactionEntries,
    { isSuccess: isCreateSuccess, data: createData },
  ] = usePostTransactionEntriesMutation();

  //#endregion

  //#region useBookings
  const { refreshList } = useDeclareNewDay();

  const {
    handleCheckOut,
    handlePendingCheckout,
    setSelectedTransactionId,
    tracesMessage,
    balanceMessage,
    tracesDialogDisclosure,
    scheduleDisclosure,
    printFolioDialogDisclosure,
    balanceDialogDisclosure,
    isLoadingCheckOut,
    printSIDialogDisclosure,
    siNo,
  } = useBookings({ refreshList });
  //#endregion

  //#region useState
  const [casARno, setCASARno] = useState<number>(0);
  const [selectedPostMenu, setSelectedPostMenu] = useState('');
  const [checkOutType, setCheckOutType] = useState('');
  const [selectedEntryType, setSelectedEntryType] = useState<string>('');
  const [selectedEntryCategory, setSelectedEntryCategory] =
    useState<string>('');
  const [optionEntryList, setOptionEntryList] = useState<string[]>([]);
  const [anchorElPrint, setAnchorElPrint] = useState<HTMLElement | null>(null);
  const [anchorCheckout, setAnchorCheckout] = useState<HTMLElement | null>(
    null,
  );
  const [anchorElPost, setAnchorElPost] = useState<HTMLElement | null>(null);
  const [remarksRequired, setRemarksRequired] = useState<boolean>(false);
  const [isNegative, setIsNegative] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isRestricted, setIsRestricted] = useState<boolean>(false);
  const [isDrCr, setDrCr] = useState<number>(0);
  const [fixAmount, setFixAmount] = useState<boolean>(false);
  const [forceClose, setForceClose] = useState<boolean>(false);

  const [privCode, setPrivCode] = useState<string>('');
  const [selectedEntryID, setSelectedEntryID] = useState<string>('');
  const [isRequireAddtConfig, setRequireAddtConfig] = useState<boolean>(false);

  const [loadingPostCLG, setloadingPostCLG] = useState(false);
  // Define a state to manage whether to show the popover or not
  const [isPopoverEnabled, setIsPopoverEnabled] = useState<boolean>(true);

  // Handle pre-approved
  const [profileId, setProfileId] = useState<string>('');
  const [profielName, setProfileName] = useState<string>('');
  const [creditLimit, setCreditLimit] = useState<number>(0);
  const [isModalGuestListOpen, setModalGuestListOpen] = useState(false);
  //#endregion

  //#region varaibles
  const totalPaybles = parseFloat(
    roomPostingLedgerList.data?.summary.totalPayables.replace(/,/g, ''),
  );
  const totalBillAmount = parseFloat(
    roomPostingLedgerList.data?.totalBillAmount,
  );
  const userData = useUser();

  const account = accountsData?.find((acc) => profileId === acc.account_id);
  const account_id = account ? account._id : null;
  const last_name = account ? account.last_name : '';
  const first_name = account ? account.first_name : '';
  //#endregion

  //#region useAppContext
  const {
    openSecCodeDialog,
    startLoading,
    stopLoading,
    genpar: { getGenparValue },
  } = useAppContext();

  const collectionDeposit = getGenparValue('collection_deposit');
  const collectionAccount = getGenparValue('collection_on_account');
  const collectionCityLedger = getGenparValue('collection_cl');
  //#endregion

  //#region useDisclosure
  const printARDisclosure = useDisclosure();
  const tracesDisclosure = useDisclosure();
  const pendingDisClosure = useDisclosure();
  const printGuestFolioDisclosure = useDisclosure();
  const cityLedgerDisclosure = useDisclosure();
  const preApprovedDisclosure = useDisclosure();
  const changeAccountDisclosure = useDisclosure();
  const creditLimitDisclosure = useDisclosure();
  const creditMemoDisclosure = useDisclosure();

  const {
    // isOpen: isLoading = true,
    isOpen: isLoading,
    onClose: onStopLoading,
  } = useDisclosure();

  //#end region

  //#region useEffect
  useEffect(() => {
    if (!entriesList?.length) {
      setOptionEntryList([]);
      return;
    }

    if (entriesList && entriesList.length > 0) {
      const status = selectedFolioStatus?.toLowerCase();

      const isTentativeOrConfirmed = [
        'tentative',
        'confirmed',
        'arrivals',
      ].includes(status);
      const isInHouseFlow = ['in-house', 'departure', 'pending'].includes(
        status,
      );

      const entryDetailedList = entriesList.map((account) => ({
        ...account,
      }));

      const mappedEntries: string[] = entryDetailedList
        .filter(
          (entry) =>
            entry.isActive && // ✅ Only include active entries
            (transactionType === 'Post Master'
              ? entry.remarks === null && entry.entryType === 'Debit'
              : entry.remarks === null),
        )
        .map((entry) => {
          if (
            entry.entryType === 'Debit' &&
            collectionDeposit &&
            collectionDeposit !== 'Invalid Genpar parameter' &&
            isTentativeOrConfirmed
          ) {
            return `${collectionDeposit}: ${entry.description}`;
          }

          if (
            entry.entryType === 'Debit' &&
            collectionAccount &&
            collectionAccount !== 'Invalid Genpar parameter' &&
            isInHouseFlow
          ) {
            return entry.description;
          }

          if (
            entry.entryType === 'Debit' &&
            collectionCityLedger &&
            collectionCityLedger !== 'Invalid Genpar parameter' &&
            transactionType === 'Post Master'
          ) {
            return `${collectionCityLedger}: ${entry.description}`;
          }

          return entry.description;
        })
        .sort((a, b) => a.localeCompare(b));

      setOptionEntryList(mappedEntries);

      // const sortedEntries = [...mappedEntries].sort();
      // const currentItem = getValues('item');

      // if (sortedEntries.length > 0 && !currentItem) {
      //   setValue('item', sortedEntries[0]);
      //   handleEntryPropertyChange(sortedEntries[0]);
      // }
    }
  }, [
    entriesList,
    collectionDeposit,
    collectionAccount,
    collectionCityLedger,
    selectedFolioStatus,
    transactionType,
    isOpen,
  ]);

  useEffect(() => {
    createData &&
      toast({
        message: 'Successfully posted entry.',
        severity: 'success',
        duration: 1200,
      });
  }, [isCreateSuccess]);

  useEffect(() => {
    setSelectedTransactionId(selectedFolioUuid);
  }, [selectedFolioUuid]);
  //#endregion

  //#region handles
  const handleOpenModalGuestList = () => {
    setModalGuestListOpen(true);
    handleClose();
  };

  const handleCloseModalGuestList = () => {
    setModalGuestListOpen(false);
  };

  const handleClickPrintOptions = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    setAnchorElPrint(event.currentTarget as HTMLElement);
  };

  const handleClosePrintOptions = () => {
    setAnchorElPrint(null);
  };

  const handleClickPostOptions = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    setAnchorElPost(event.currentTarget as HTMLElement);
  };

  const handleClosePostOptions = () => {
    setAnchorElPost(null);
  };

  const handleClickCheckOutOptions = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    setAnchorCheckout(event.currentTarget as HTMLElement);
  };

  const handleCloseCheckOutOptions = () => {
    setAnchorCheckout(null);
  };

  const handleApplyCreditMemo = async () => {
    //skip validation and check out
    const isSuccessCheckOut = await handleCheckOut(true, true, false);
    isSuccessCheckOut && onCloseForm(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit(handleRoomPosting)();
    }
  };

  const handleEntryPropertyChange = (newValue: string) => {
    const selectedEntry = entriesList.find((entry) => {
      const descriptionPart = newValue?.includes(': ')
        ? newValue.split(': ')?.[1]
        : newValue;

      return entry.description === descriptionPart;
    });

    //return if no description
    if (!selectedEntry) return;

    setValue('gross_amount', Number(selectedEntry.amount));
    setFixAmount(selectedEntry.isFixAmount);
    setSelectedEntryType(selectedEntry.description);
    setSelectedEntryCategory(selectedEntry.clearingCategory);
    setIsPopoverEnabled(selectedEntry.entryType !== 'Debit');
    setDrCr(
      selectedEntry.entryType === 'Debit'
        ? 1
        : selectedEntry.entryType === 'Credit'
          ? 2
          : 0,
    );
    setRemarksRequired(selectedEntry.isRequireRemarks);
    setIsNegative(selectedEntry.isNegative);
    setIsRestricted(selectedEntry.isRestricted);
    setPrivCode(selectedEntry.privCode);
    setSelectedEntryID(selectedEntry.referenceID);

    setRequireAddtConfig(selectedEntry.isRequireAddtConfig);

    // Auto set amount if has any CL payment breakdown
    if (clEditedRows.length > 0) {
      const totalCurrentCollection = clEditedRows.reduce((sum, row) => {
        const current =
          Number(String(row.currentCollection).replace(/,/g, '')) || 0;
        const original =
          Number(String(row.originalCollection).replace(/,/g, '')) || 0;
        return sum + (current - original);
      }, 0);

      setValue('gross_amount', Number(totalCurrentCollection));
    }
  };
  //#endregion

  //#region handleRoomPosting
  const handleRoomPosting: SubmitHandler<RoomPostingEntryBody> = async (
    data,
  ) => {
    if (isRequireAddtConfig) {
      handleToggle();
      return;
    }

    if (isRestricted) {
      openSecCodeDialog(async (result) => {
        if (result?.data.isValid) {
          await processRoomPosting(data);
        } else {
          return;
        }
      }, privCode);
    } else {
      await processRoomPosting(data);
    }
  };

  const processRoomPosting = async (data: RoomPostingEntryBody) => {
    const parsedAmount = parseFloat(
      String(data.gross_amount).replace(/,/g, ''),
    );

    if (
      isDrCr === 1 &&
      parsedAmount > totalPaybles &&
      transactionType === 'Post Master'
    ) {
      toast({
        severity: 'warning',
        message: 'Payment amount cannot exceed total payables.',
        duration: 1200,
      });
      onStopLoading();
      setIsProcessing(false);
      return;
    }

    const propertyID = selectedProperty.value;
    const transactionIdentifier = selectedFolioUuid;
    const subGroupId = selectedSubGroup;
    const cutOffDate = moment(currentSalesDate).format('YYYY-MM-DD');
    const dateCheckin = roomPostingLedgerList.data?.dateCheckin;
    const bookingStatus = roomPostingLedgerList.data?.bookingStatus;
    const lineRemarks = String(data.line_remarks);
    const isReservation =
      bookingStatus === 'Tentative' || bookingStatus === 'Confirmed';
    const isReservationAndCharges = isDrCr === 2 && isReservation;

    let mappingType = 4;
    let transType = 4;
    switch (isDrCr) {
      case 1: //DR
        // transType = 4;
        switch (transactionType) {
          case 'Room Posting':
            {
              transType = 1;
              if (isReservation) {
                // Deposits
                mappingType = 3;
              } else {
                // On Account
                mappingType = 1;
              }
            }
            break;
          case 'Post Master': //City Ledger
            transType = 4;
            mappingType = 2;
            break;
          case 'Reservation': //Deposits
            transType = 1;
            mappingType = 3;
            break;
        }
        break;
      case 2: //CR
        transType = 1;
        mappingType = 4; //Others
        break;
      default: //ADJ
        mappingType = 4;
        break;
    }

    setIsProcessing(true);

    const jsonBody: RoomPostingTransactionEntry = {
      properties: [
        {
          property: {
            propertyID: propertyID,
            entries: [
              {
                entry: {
                  transaction_identifier: transactionIdentifier,
                  cutoff_date: isReservationAndCharges
                    ? dateCheckin
                    : cutOffDate,
                  quantity: 1,
                  gross_amount: parsedAmount,
                  mapping_type: mappingType,
                  module_id: 1,
                  transaction_type: transType,
                  dr_cr_adj: isDrCr,
                  item: selectedEntryID,
                  discount: 0,
                  discount_type: 1,
                  sub_group: subGroupId,
                  category_type: 1,
                  line_remarks: lineRemarks,
                  discount_remarks: '',
                  payment_details: {},
                },
              },
            ],
          },
        },
      ],
    };

    // Compare total edited collections vs amount on total payment
    if (clEditedRows.length > 0) {
      const totalCurrentCollection = clEditedRows.reduce((sum, row) => {
        const current =
          Number(String(row.currentCollection).replace(/,/g, '')) || 0;
        const original =
          Number(String(row.originalCollection).replace(/,/g, '')) || 0;
        return sum + (current - original);
      }, 0);

      if (totalCurrentCollection !== parsedAmount) {
        toast({
          severity: 'warning',
          message:
            'There’s a discrepancy between the folio breakdown amounts and the amount being posted. Kindly review the posted amounts.',
        });

        onStopLoading();
        setIsPopoverEnabled(false);
        setIsProcessing(false);
        return;
      }
    }
    try {
      const postEntryResponse = await postTransactionEntries({
        ...jsonBody,
        invalidateTags: isSpecificBooking,
      }).unwrap();

      let ledger_id = 0;

      if (postEntryResponse?.data?.[0]?.result) {
        ledger_id = postEntryResponse.data[0].result;
      }

      toast({
        severity: 'success',
        message: 'Successfully posted entry.',
        duration: 1000,
      });

      // Post CAS AR for payments if necessary
      if (isDrCr === 1 && ledger_id > 0) {
        const cas_id = await postCASTransaction('AR', ledger_id);
        if (cas_id > 0) {
          setCASARno(cas_id);
          printARDisclosure.onOpen();
        }
      }

      // Check if has CL payment breakdown
      if (clEditedRows.length > 0) {
        const jsonCLPaymentBreakdown = {
          properties: [
            {
              property: {
                propertyID: propertyID,
                cityLedgerPaymentBreakdowns: {
                  collectionLedgerID: ledger_id,
                  cityLedgerPaymentBreakdown: clEditedRows.map((i) => ({
                    ledgerTransactionID: i.ledgerTransactionID,
                    currentCollection:
                      Number(String(i.currentCollection).replace(/,/g, '')) ||
                      0,
                    originalCollection:
                      Number(String(i.originalCollection).replace(/,/g, '')) ||
                      0,
                  })),
                },
              },
            },
          ],
        };

        try {
          await postCityLedgerPaymentBreakdown(jsonCLPaymentBreakdown).unwrap();
          setCLEditedRows([]);
        } catch (err) {
          console.error('Failed to post breakdown:', err);
        }
      }

      reset();
      ledgerRefetch();
    } catch (err) {
      toast({
        severity: 'error',
        message: err,
      });
    } finally {
      onStopLoading();
      setIsPopoverEnabled(false);
      setIsProcessing(false);
    }
  };
  //#endregion

  //#region handleCloseAccount
  const handleCloseAccount = async (accountReferenceID: string) => {
    if (!selectedFolioUuid) {
      toast({
        severity: 'warning',
        message: 'No room or folio selected for checkout.',
        duration: 1000,
      });
      return;
    }

    if (totalPaybles > 0) {
      toast({
        severity: 'warning',
        message: 'Close Out is not permitted with an outstanding balance.',
        duration: 1200,
      });
      return;
    }
    await closeCLAccount(accountReferenceID)
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: 'City Ledger account closed successfully.',
        });
      })
      .catch((err) => {
        toast({
          severity: 'error',
          message: err,
        });
      });
  };
  //#endregion

  //#region postCASTransaction
  const postCASTransaction = async (
    cas_type: string,
    transaction_id: number | string,
  ): Promise<number> => {
    const propertyID = selectedProperty.value;
    const jsonCASBody: CASPostTransaction = {
      properties: [
        {
          property: {
            propertyID: propertyID,
            cas_transaction: [
              {
                module_code: moduleSelected,
                cas_type: cas_type,
                source: 'Room Posting',
                transaction_id: transaction_id,
                guest_id: roomPostingLedgerList?.data?.guestID || 0,
              },
            ],
          },
        },
      ],
    };

    try {
      const casResponse = await postCASTransactionAPI(jsonCASBody).unwrap();
      // Extract the casResponse
      if (casResponse?.data?.[0]?.[0]?.result) {
        return casResponse.data[0][0].result;
      } else {
        return 0;
      }
    } catch (err) {
      toast({ severity: 'error', message: err });
      throw err;
    }
  };
  //#endregion

  //#region handleRoomCheckOut
  const handleRoomCheckOut = async () => {
    if (!selectedFolioUuid) {
      toast({
        severity: 'warning',
        message: 'No room or folio selected for checkout.',
        duration: 1000,
      });
      return;
    }

    if (totalPaybles > 0) {
      toast({
        severity: 'warning',
        message: 'Check Out with an outstanding balance is not permitted.',
        duration: 1200,
      });
      return;
    } else if (totalPaybles < 0) {
      //Show credit Memo
      setCheckOutType('CheckOut');
      creditMemoDisclosure.onOpen();
      return;
    }

    try {
      const isSuccessCheckOut = await handleCheckOut(
        false,
        false,
        false,
        totalBillAmount != 0 ? true : false,
      );

      isSuccessCheckOut && onCloseForm(false);
      isSuccessCheckOut && handleCloseResvDrawer?.();
    } catch (err) {
      const error = err as Error;
      toast({
        severity: 'warning',
        message: error?.message,
        duration: 1200,
      });
    }
  };
  //#endregion

  //#region handleOnClickCityLedger

  const handleOnClickCityLedger = async () => {
    try {
      startLoading();
      if (totalPaybles === 0) {
        toast({
          severity: 'warning',
          message: 'Check out as City Ledger with zero balance not permitted.',
          duration: 1200,
        });
        return;
      } else if (totalPaybles < 0) {
        setCheckOutType('CheckOutCL');
        creditMemoDisclosure.onOpen();
        return;
      }

      const preApproved = await checkPreApprovedAccount();
      if (preApproved) {
        preApprovedDisclosure.onOpen();
      } else {
        const isClHotel = !!roomPostingLedgerList.data?.isClHotel;
        if (isClHotel) {
          setProfileId(roomPostingLedgerList?.data?.profileID);
          changeAccountDisclosure.onOpen();
        } else {
          handleOpenModalGuestList();
        }
      }
    } catch (error) {
    } finally {
      stopLoading();
    }
  };
  //#endregion

  //#region checkPreApprovedAccount
  const checkPreApprovedAccount = async () => {
    try {
      if (!selectedFolioUuid) {
        return;
      }
      const response = await getPreApprovedList(
        `/${selectedFolioUuid}`,
      ).unwrap();
      const accounts = response?.data;

      if (accounts.length > 0) {
        accounts.forEach((profileWrapper) => {
          const account = profileWrapper;
          setProfileId(account.accountId);
          setProfileName(account.accountName);
          setCreditLimit(account.creditLimit);
        });
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error fetching pre-approved details:', error);
      return false;
    }
  };
  //#endregion

  //#region handleCreditLimitPreApprove
  const handleCreditLimitPreApprove = () => {
    if (creditLimit === 0) {
      cityLedgerDisclosure.onOpen();
    } else if (totalPaybles > creditLimit) {
      creditLimitDisclosure.onOpen();
    } else {
      cityLedgerDisclosure.onOpen();
    }
  };
  //#endregion

  //#region handleCreditLimitOnAccount
  const handleCreditLimitOnAccount = () => {
    const creditLimit = roomPostingLedgerList.data?.creditLimit;
    if (totalPaybles > creditLimit) {
      creditLimitDisclosure.onOpen();
    } else {
      cityLedgerDisclosure.onOpen();
    }
  };
  //#endregion

  //#region postCityLedgerApprovalEntries
  const postCityLedgerApprovalEntries = async () => {
    await openSecCodeDialog(async (result) => {
      try {
        if (result?.data.isValid) {
          setloadingPostCLG(true);
          const propertyID = selectedProperty.value;
          const transactionIdentifier = selectedFolioUuid;
          const cutOffDate = moment(currentSalesDate).format('YYYY-MM-DD');
          const approvedBy = userData?.name;
          const folioNo = roomPostingLedgerList.data?.folioNo;
          const rateTotalAmount = roomPostingLedgerList.data?.rateTotalAmount;
          const dateCheckin = roomPostingLedgerList.data?.dateCheckin;
          const dateCheckout = roomPostingLedgerList.data?.dateCheckout;

          const cityLedgerEntry = {
            properties: [
              {
                property: {
                  propertyID: propertyID,
                  ledgers: [
                    {
                      ledger: {
                        booking_reference_id: transactionIdentifier,
                        account_id: profileId,
                        folio_number: folioNo,
                        approved_by: approvedBy,
                        sales_date: cutOffDate,
                        rate_total_amount: rateTotalAmount,
                        date_checkin: dateCheckin,
                        date_checkout: dateCheckout,
                      },
                    },
                  ],
                },
              },
            ],
          };

          await postCityLedgerApproval({
            ...cityLedgerEntry,
            invalidateTags: isSpecificBooking,
          }).unwrap();
          await postCityLedger();
        } else {
          reset();
          return;
        }
      } catch (err) {
        toast({
          severity: 'error',
          message: err,
        });
      } finally {
        cityLedgerDisclosure.onClose();
        setloadingPostCLG(false);
      }
    }, 'cl_cout');
  };
  //#endregion

  //#region postCityLedger
  const postCityLedger = async () => {
    const propertyID = selectedProperty.value;
    const transactionIdentifier = selectedFolioUuid;
    const subGroupId = selectedSubGroup;
    const cutOffDate = moment(currentSalesDate).format('YYYY-MM-DD');

    const trfAmount = '-' + totalPaybles;
    const clAmount = totalPaybles;

    const trfId =
      entriesList.find((entry) => entry.remarks === 'TRF')?.referenceID || '';

    const cityLedgerId =
      entriesList.find((entry) => entry.remarks === 'CL')?.referenceID || '';

    const bookingID = roomPostingLedgerList.data?.ledgerResult?.[0]?.booking_id;

    if (trfId === '') {
      toast({
        severity: 'warning',
        message: 'Please check the default entries for TRF.',
        duration: 1200,
      });
      return;
    } else if (cityLedgerId === '') {
      toast({
        severity: 'warning',
        message: 'Please check the default entries for City Ledger.',
        duration: 1200,
      });
      return;
    }

    if (trfId === '') {
      toast({
        severity: 'warning',
        message: 'Please check the default entries for TRF.',
        duration: 1200,
      });
      return;
    } else if (cityLedgerId === '') {
      toast({
        severity: 'warning',
        message: 'Please check the default entries for City Ledger.',
        duration: 1200,
      });
      return;
    }

    setIsProcessing(true);

    const entry = {
      transaction_identifier: transactionIdentifier,
      cutoff_date: cutOffDate,
      quantity: 1,
      mapping_type: 2, // City Ledger
      module_id: 1,
      discount: 0,
      discount_type: 1,
      sub_group: subGroupId,
      category_type: 1,
      line_remarks: '',
      discount_remarks: '',
      payment_details: {},
    };

    const cityLedgerBody = {
      properties: [
        {
          property: {
            propertyID: propertyID,
            entries: [
              {
                entry: {
                  ...entry,
                  gross_amount: trfAmount,
                  item: trfId,
                  dr_cr_adj: 2, // CR
                  transaction_type: 1, // Rooms
                  line_remarks: `ACN:${String(account_id).padStart(11, '0')} - ${last_name},${first_name}`,
                },
              },
              {
                entry: {
                  ...entry,
                  transaction_identifier: profileId,
                  gross_amount: clAmount,
                  item: cityLedgerId,
                  dr_cr_adj: 1, // DR
                  transaction_type: 4, // Account
                  line_remarks: `BKN:${String(bookingID).padStart(11, '0')} - ${selectedGuestName}`,
                },
              },
            ],
          },
        },
      ],
    };

    try {
      await postTransactionEntries({
        ...cityLedgerBody,
        invalidateTags: isSpecificBooking,
      }).unwrap();
      //Check out and Print Sales Invoice
      const isSuccessCheckOut = await handleCheckOut(true, true, false);
      isSuccessCheckOut && onCloseForm(false);

      toast({
        severity: 'success',
        message: 'Check out as City Ledger was successful.',
        duration: 1200,
      });
      reset();
    } catch (err) {
      toast({
        severity: 'error',
        message: err,
      });
    }
  };
  //#endregion

  //#region useForm
  const methods = useForm<RoomPostingEntryBody>({
    defaultValues: {
      quantity: 1,
      gross_amount: 0,
      item: '',
      line_remarks: '',
    },
    resolver: yupResolver<RoomPostingEntryBody>(
      RoomPostingEntrySchema(3, remarksRequired),
    ),
    mode: 'onSubmit',
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    getValues,
  } = methods;
  //#endregion

  return (
    <>
      <Box>
        {!isLoading ? (
          <Box>
            {/* GROUP INPUT */}
            <form id='room-posting' onSubmit={handleSubmit(handleRoomPosting)}>
              <Stack
                id='room-posting-form'
                sx={{
                  p: 2,
                  borderTop: '1px dashed lightgray',
                  borderBottom: '1px dashed lightgray',
                  opacity: isNetLocked ? 0.5 : 'unset',
                  pointerEvents: isNetLocked ? 'none' : 'unset',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                direction={{ xs: 'column', lg: 'row' }}
                gap={1}
              >
                <AutoCompleteController
                  label='Entry Type'
                  name='item'
                  options={optionEntryList.sort() || []}
                  getOptionLabel={(option) => option || ''}
                  control={control}
                  errors={errors}
                  textFieldProps={{
                    sx: {
                      width: { xs: '100%', lg: '100%' },
                      minWidth: { lg: '230px' },
                    },
                    size: 'small',
                  }}
                  disabled={
                    isProcessing ||
                    isLoadingCheckOut ||
                    !selectedFolioUuid ||
                    open
                  }
                  onChange={(value: string) => handleEntryPropertyChange(value)}
                  style={{ margin: 0 }}
                />

                <Stack
                  direction='row'
                  alignItems='center'
                  gap={1}
                  width={{ xs: '100%', l: 'auto' }}
                >
                  <InputFieldsController
                    label='Amount'
                    name='gross_amount'
                    control={control}
                    errors={errors}
                    inputProps={{
                      style: { textAlign: 'right' },
                      onFocus: () => true,
                      maxLength: 12,
                    }}
                    InputProps={{
                      inputComponent: (props: CustomNumberFormatProps) => (
                        <CustomNumberFormat
                          {...props}
                          allowNegative={isNegative}
                          isNegative={isNegative}
                        />
                      ),
                    }}
                    wrapperStyle={{
                      minWidth: '200px',
                      width: '100%',
                      marginBottom: 0,
                    }}
                    size='small'
                    autoComplete='on'
                    disabled={
                      fixAmount ||
                      isProcessing ||
                      isLoadingCheckOut ||
                      !selectedFolioUuid
                    }
                    onKeyDown={handleKeyDown}
                    onFocus={(event) => {
                      event.target.select();
                    }}
                  />

                  {!isRequireAddtConfig ? (
                    <IconButton
                      sx={{
                        width: '20px',
                        border: 'none',

                        p: 0,
                      }}
                      ref={anchorRef}
                      onClick={() => {}}
                      disabled={true}
                    >
                      <LaunchIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      sx={{
                        width: '20px',
                        border: 'none',

                        p: 0,
                      }}
                      ref={anchorRef}
                      onClick={() => {
                        handleToggle();
                      }}
                      disabled={isPopoverEnabled}
                    >
                      <LaunchIcon />
                    </IconButton>
                  )}
                </Stack>

                <InputFieldsController
                  control={control}
                  errors={errors}
                  label={remarksRequired ? 'Remarks *' : 'Remarks'}
                  name='line_remarks'
                  wrapperStyle={{
                    marginBottom: 0,
                  }}
                  size='small'
                  inputProps={{ maxLength: 100, onFocus: () => true }}
                  onKeyDown={handleKeyDown}
                  disabled={
                    isProcessing || isLoadingCheckOut || !selectedFolioUuid
                  }
                />
              </Stack>
            </form>
            {/* GROUP BUTTON */}
            <Stack
              sx={{
                p: 2,
                justifyContent: 'flex-end',
                opacity: isNetLocked ? 0.5 : 'unset',
                pointerEvents: isNetLocked ? 'none' : 'unset',
              }}
              direction={{ xs: 'column', lg: 'row' }}
              gap={1}
            >
              <Stack
                gap={1}
                direction={{ xs: 'column', sm: 'row' }}
                sx={{
                  height: '42px',
                  ml: { xs: '0', sm: 'auto' },
                }}
              >
                {/* POST BUTTON */}
                <Stack direction='row'>
                  <LoadingButton
                    variant='contained'
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      borderTopRightRadius: '0',
                      borderBottomRightRadius: '0',
                      justifyContent: { xs: 'left', md: 'center' },
                    }}
                    form='room-posting'
                    onClick={handleSubmit(handleRoomPosting)}
                    loading={isProcessing}
                    disabled={
                      isProcessing || isLoadingCheckOut || !selectedFolioUuid
                    }
                  >
                    Post
                  </LoadingButton>

                  <Button
                    variant='contained'
                    sx={{
                      width: '10px',
                      borderTopLeftRadius: '0',
                      borderBottomLeftRadius: '0',
                      minWidth: '0',
                    }}
                    startIcon={<ArrowDropDownIcon />}
                    disabled={
                      isProcessing || isLoadingCheckOut || !selectedFolioUuid
                    }
                    onClick={handleClickPostOptions}
                  />
                </Stack>

                {/* VOID BUTTON */}
                <Button
                  sx={{
                    width: { xs: '100%', sm: '50px', lg: '80px' },
                    height: '42px',
                    backgroundColor: '#DFE3E8',
                    justifyContent: { xs: 'left', md: 'center' },
                  }}
                  disabled={
                    isProcessing || isLoadingCheckOut || !selectedFolioUuid
                  }
                  onClick={handleVoidItem}
                >
                  Void
                </Button>

                {/* PRINT */}
                {!isCityLedgerPosting && (
                  <Stack direction='row'>
                    <LoadingButton
                      sx={{
                        width: { xs: '100%', lg: '50px' },
                        backgroundColor: '#DFE3E8',
                        borderTopRightRadius: '0',
                        borderBottomRightRadius: '0',

                        justifyContent: { xs: 'left', md: 'center' },
                      }}
                      disabled={
                        isProcessing || isLoadingCheckOut || !selectedFolioUuid
                      }
                      onClick={() => {
                        printGuestFolioDisclosure.onOpen();
                      }}
                    >
                      Print
                    </LoadingButton>
                    <Button
                      sx={{
                        width: '10px',
                        backgroundColor: '#DFE3E8',
                        borderTopLeftRadius: '0',
                        borderBottomLeftRadius: '0',
                        minWidth: '0',
                      }}
                      startIcon={<ArrowDropDownIcon />}
                      disabled={
                        isProcessing || isLoadingCheckOut || !selectedFolioUuid
                      }
                      onClick={handleClickPrintOptions}
                    />
                  </Stack>
                )}

                {/* CHECKOUT BUTTON */}
                {isCityLedgerPosting ? (
                  <LoadingButton
                    sx={{
                      width: { xs: '100%', lg: '120px' },
                      height: '42px',
                      backgroundColor: '#DFE3E8',
                      justifyContent: { xs: 'left', md: 'center' },
                    }}
                    onClick={() => {
                      handleCloseAccount(selectedFolioUuid);
                      onCloseForm(false);
                    }}
                    disabled={
                      isProcessing || isLoadingCheckOut || !selectedFolioUuid
                    }
                  >
                    Close Out
                  </LoadingButton>
                ) : (
                  <Stack direction='row' id='room-posting_checkout-btn'>
                    <LoadingButton
                      sx={{
                        width: { xs: '100%', lg: '120px' },
                        backgroundColor: '#DFE3E8',
                        borderTopRightRadius: '0',
                        borderBottomRightRadius: '0',
                        justifyContent: { xs: 'left', md: 'center' },
                        whiteSpace: 'nowrap',
                      }}
                      onClick={handleRoomCheckOut}
                      disabled={
                        (totalPaybles < 0 &&
                          ['in-house', 'departures'].includes(
                            selectedFolioStatus.toLowerCase(),
                          )) ||
                        isProcessing ||
                        isLoadingCheckOut ||
                        selectedFolioUuid === '' ||
                        ![
                          'in-house',
                          'occupied',
                          'pending',
                          'departures',
                        ].includes(selectedFolioStatus.toLowerCase()) ||
                        roomPostingLedgerList.data?.summary.totalCharges >
                          roomPostingLedgerList.data?.summary.totalCollection
                      }
                      loading={isLoadingCheckOut}
                    >
                      Check Out
                    </LoadingButton>
                    <Button
                      id='room-posting_checkout-more'
                      sx={{
                        width: '10px',
                        backgroundColor: '#DFE3E8',
                        borderTopLeftRadius: '0',
                        borderBottomLeftRadius: '0',
                        minWidth: '0',
                      }}
                      startIcon={<ArrowDropDownIcon />}
                      // disabled={
                      //   isProcessing ||
                      //   isLoadingCheckOut ||
                      //   (['in-house', 'occupied', 'departures'].includes(
                      //     selectedFolioStatus.toLowerCase(),
                      //   ) &&
                      //     selectedFolioUuid !== '')
                      // }
                      onClick={handleClickCheckOutOptions}
                    />
                  </Stack>
                )}

                {/* CLOSE BUTTON */}
                <Button
                  {...(isCityLedgerPosting
                    ? { variant: 'outlined' } // When true, apply outlined variant
                    : {
                        sx: {
                          width: { xs: '100%', sm: 'auto', lg: '80px' },
                          backgroundColor: '#DFE3E8',
                          justifyContent: { xs: 'left', md: 'center' },
                        },
                      })}
                  onClick={() => {
                    onCloseForm(false);
                  }}
                >
                  Close
                </Button>
              </Stack>
            </Stack>
          </Box>
        ) : (
          <Skeleton
            animation='wave'
            variant='rectangular'
            sx={[{ borderRadius: '4px', height: '100%' }]}
          />
        )}
      </Box>

      {/* MENUS , MODALS , DIALOGS */}
      <>
        {/* POST MENU */}
        <Menu
          id='simple-menu'
          anchorEl={anchorElPost}
          keepMounted
          open={Boolean(anchorElPost)}
          onClose={handleClosePostOptions}
        >
          {/* Hide Temporarily #BF-11459 */}
          {/* <MenuItem
            onClick={() => {
              handleClosePostOptions();
              setSelectedPostMenu('CMU');
              modalDisclosure.onToggle();
            }}
          >
            Credit Memo
          </MenuItem> */}
          <MenuItem
            onClick={() => {
              handleClosePostOptions();
              setSelectedPostMenu('CWT');
              modalDisclosure.onToggle();
            }}
          >
            CWT/CVAT
          </MenuItem>
        </Menu>

        {/* PRINT MENU */}
        <Menu
          id='simple-menu'
          anchorEl={anchorElPrint}
          keepMounted
          open={Boolean(anchorElPrint)}
          onClose={handleClosePrintOptions}
        >
          <MenuItem onClick={handleClosePrintOptions} disabled>
            Send Folio to Email
          </MenuItem>
          <MenuItem onClick={handleClosePrintOptions} disabled>
            Send Folio to Table
          </MenuItem>
        </Menu>

        {/* CHECKOUT MENU */}
        {!['confirmed', 'tentative'].includes(
          selectedFolioStatus?.toLowerCase(),
        ) && (
          <Menu
            id='checkout-menu'
            anchorEl={anchorCheckout}
            keepMounted
            open={Boolean(anchorCheckout)}
            onClose={handleCloseCheckOutOptions}
          >
            {totalPaybles >= 0 && (
              <MenuItem
                id='city-ledger-menu'
                onClick={() => {
                  handleOnClickCityLedger();
                  handleCloseCheckOutOptions();
                }}
              >
                City Ledger
              </MenuItem>
            )}
            <MenuItem
              id='pending-menu'
              onClick={() => {
                pendingDisClosure.onOpen();
                handleCloseCheckOutOptions();
              }}
              disabled={selectedFolioStatus.toLowerCase() === 'pending'}
            >
              Pending
            </MenuItem>
            {totalPaybles >= 0 && (
              <MenuItem onClick={handleRoomCheckOut}>
                Check Out and Send Folio to Email
              </MenuItem>
            )}
          </Menu>
        )}
        {/* {Room Posting Entries More Options */}
        <PopoverWrapper disableOnClose>
          <RoomPostingPopover
            optionsEntry={selectedEntryType}
            optionsEntryCategory={selectedEntryCategory}
            optionsEntryAmount={getValues('gross_amount').toString()}
            optionsEntryID={selectedEntryID}
            onPopOverClose={() => {
              handleClose();
              setValue('item', '');
              setValue('gross_amount', 0);
              setValue('line_remarks', '');
            }}
            totalPayables={totalPaybles}
          />
        </PopoverWrapper>

        <ScheduleDialog
          onConfirm={async () => {
            const isSuccessCheckOut = await handleCheckOut(true, true, false);
            isSuccessCheckOut && onCloseForm(false);
            scheduleDisclosure.onClose();
            handleCloseResvDrawer?.();
          }}
          open={scheduleDisclosure.isOpen}
          desc={
            <Stack gap='8px' sx={{ minWidth: { sx: 'auto', md: '300px' } }}>
              <Typography>
                This guest is not currently scheduled for check out, would you
                like to continue?
              </Typography>
            </Stack>
          }
          title='Confirm Action'
          cancelButtonTitle='No'
          confirmButtonTitle='Yes'
          onClose={() => {
            scheduleDisclosure.onClose();
          }}
        />

        <PendingDialog
          onConfirm={async () => {
            const isSuccessCheckOut = await handlePendingCheckout(false, false);
            isSuccessCheckOut && onCloseForm(false);
            pendingDisClosure.onClose();
          }}
          open={pendingDisClosure.isOpen}
          desc={
            <Stack gap='8px' sx={{ minWidth: { sx: 'auto', md: '300px' } }}>
              <Typography>
                Are you sure you want to check out this folio as pending?
              </Typography>
            </Stack>
          }
          title='Confirm Action'
          cancelButtonTitle='No'
          confirmButtonTitle='Yes'
          onClose={pendingDisClosure.onClose}
        />

        <TracesDialog
          open={tracesDialogDisclosure.isOpen}
          onConfirm={async () => {
            const isSuccessCheckOut = await handleCheckOut(false, true, false);
            isSuccessCheckOut && onCloseForm(false);
            tracesDialogDisclosure.onClose();
          }}
          desc={
            <Stack gap='8px' sx={{ minWidth: { sx: 'auto', md: '300px' } }}>
              <Typography
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(tracesMessage),
                }}
              />
            </Stack>
          }
          title='Confirm Action'
          cancelButtonTitle='View'
          confirmButtonTitle='Skip'
          onClose={() => {
            tracesDisclosure.onOpen();
            tracesDialogDisclosure.onClose();
          }}
        />

        <PrintFolioDialog
          open={printFolioDialogDisclosure.isOpen}
          onConfirm={async () => {
            // Inject print code here
            setForceClose(true);
            printFolioDialogDisclosure.onOpen();
          }}
          desc={
            <Stack gap='8px' sx={{ minWidth: { sx: 'auto', md: '300px' } }}>
              <Typography>Would you like to print the Guest Folio?</Typography>
            </Stack>
          }
          title='Confirm Folio Print'
          cancelButtonTitle='No'
          confirmButtonTitle='Yes'
          icon={PrintOutlined}
          onClose={() => {
            printFolioDialogDisclosure.onClose();
            onCloseForm(true);
          }}
        />

        <ConfirmationDialog
          onConfirm={postCityLedgerApprovalEntries}
          open={cityLedgerDisclosure.isOpen}
          desc={
            <Stack gap='8px' sx={{ minWidth: { sx: 'auto', md: '300px' } }}>
              <Typography>
                Are you sure you want to check out this folio as city ledger?
              </Typography>
            </Stack>
          }
          title='Confirm Action'
          cancelButtonTitle='No'
          confirmButtonTitle='Yes'
          onClose={() => {
            cityLedgerDisclosure.onClose();
            setloadingPostCLG(false);
          }}
          loading={loadingPostCLG}
        />

        <PreApprovedDialog
          open={preApprovedDisclosure.isOpen}
          desc={
            <Stack gap='8px' sx={{ minWidth: { sx: 'auto', md: '300px' } }}>
              <Typography>
                This Folio is currently pre-approved for City Ledger to
              </Typography>
              <Typography>
                Guest Name: <strong>{profielName}</strong>
              </Typography>
              <Typography>
                Click <strong>Yes</strong> to proceed or <strong>No</strong> to
                select another Account.
              </Typography>
            </Stack>
          }
          title='Confirm Account'
          cancelButtonTitle='No'
          confirmButtonTitle='Yes'
          icon={QuestionMarkOutlined}
          onConfirm={async () => {
            handleCreditLimitPreApprove();
            preApprovedDisclosure.onClose();
          }}
          onClose={() => {
            handleOpenModalGuestList();
            preApprovedDisclosure.onClose();
          }}
        />

        <ChangeAccountDialog
          open={changeAccountDisclosure.isOpen}
          desc={
            <Stack gap='8px' sx={{ minWidth: { sx: 'auto', md: '300px' } }}>
              <Typography>
                Would you like to select a different account to charge <br />
                this City Ledger?
              </Typography>
              <Typography>
                Click <strong>Yes</strong> to select another Account or{' '}
                <strong>No</strong> to proceed <br /> with this account.
              </Typography>
            </Stack>
          }
          title='Confirm Account'
          cancelButtonTitle='No'
          confirmButtonTitle='Yes'
          icon={QuestionMarkOutlined}
          onConfirm={async () => {
            handleOpenModalGuestList();
            changeAccountDisclosure.onClose();
          }}
          onClose={() => {
            handleCreditLimitOnAccount();
            changeAccountDisclosure.onClose();
          }}
        />

        <DialogInformationV2
          open={balanceDialogDisclosure.isOpen}
          desc={
            <Stack gap='8px' sx={{ minWidth: { sx: 'auto', md: '300px' } }}>
              <Typography>{balanceMessage}</Typography>
            </Stack>
          }
          title='Not Allowed'
          onClose={() => {
            balanceDialogDisclosure.onClose();
          }}
          additionalBtn={{
            title: 'Room Posting',
            onClick: () => {
              handleToggle();
            },
          }}
        />

        <TracesListModal
          referenceID={selectedFolioUuid}
          isOpen={tracesDisclosure.isOpen}
          onClose={tracesDisclosure.onClose}
        />

        {/* GuestFolio */}
        <GuestFolioModal
          {...{
            bookingReferenceId: selectedFolioUuid,
            isOpen: printGuestFolioDisclosure.isOpen,
            handleModalClose: () => {
              printGuestFolioDisclosure.onClose();
              forceClose && onCloseForm(true);
            },
            subGroupValue,
            selectedSubGroup,
          }}
        />

        <FOGuestListTable
          isOpen={isModalGuestListOpen}
          onClose={handleCloseModalGuestList}
          onAccount={(profileId, profileName) => {
            setProfileId(profileId);
            setProfileName(profileName);
          }}
          postCityLedger={cityLedgerDisclosure.onOpen}
          totalPayables={totalPaybles}
        />

        <CreditLimitDialog
          open={creditLimitDisclosure.isOpen}
          desc={
            <Stack gap='8px' sx={{ minWidth: { sx: 'auto', md: '300px' } }}>
              <Typography>
                The CL amount exceeds the credit limit amount. <br />
              </Typography>
              <Typography>
                Would you like to continue? Click <strong>Yes</strong> to
                Proceed
                <br /> or <strong>No</strong> to cancel the transaction.
              </Typography>
            </Stack>
          }
          title='Confirm'
          cancelButtonTitle='No'
          confirmButtonTitle='Yes'
          icon={QuestionMarkOutlined}
          onConfirm={async () => {
            cityLedgerDisclosure.onOpen();
            creditLimitDisclosure.onClose();
          }}
          onClose={() => {
            creditLimitDisclosure.onClose();
          }}
        />

        <CreditMemoDialog
          open={creditMemoDisclosure.isOpen}
          desc={
            <Stack gap='8px' sx={{ minWidth: { sx: 'auto', md: '310px' } }}>
              <Typography>
                Check out {checkOutType === 'CheckOut' ? '' : 'as City Ledger'}{' '}
                with over-payment is not permitted.
                <br />
              </Typography>
              <Typography>
                Would you like to apply <strong>Credit Memo</strong> for the
                <br /> amount of
                <strong> {formatNumber(totalPaybles * -1, 2, 2)}</strong>?{' '}
                <br />
                <br />
                Click <strong>Yes</strong> to proceed or <strong>No</strong> to
                cancel.
              </Typography>
            </Stack>
          }
          title='Credit Memo'
          cancelButtonTitle='No'
          confirmButtonTitle='Yes'
          icon={QuestionMarkOutlined}
          onConfirm={async () => {
            creditMemoDisclosure.onClose();
            setSelectedPostMenu('CM');
            modalDisclosure.onToggle();
          }}
          onClose={() => {
            creditMemoDisclosure.onClose();
          }}
        />

        <CASARVoucher
          ar_no={casARno}
          isOpen={printARDisclosure.isOpen}
          onClose={() => {
            printARDisclosure.onClose();
          }}
        />

        <CASSIVoucher
          si_no={siNo}
          isOpen={printSIDialogDisclosure.isOpen}
          onClose={() => {
            printSIDialogDisclosure.onClose();
            onCloseForm(false);
          }}
        />

        <ModalWrapper
          {...{
            modalDisclosure,
            disableOnClose: true,
            withHeader: {
              title:
                selectedPostMenu === 'CWT'
                  ? 'Tax Credit Posting'
                  : selectedPostMenu === 'CMU' || selectedPostMenu === 'CM'
                    ? 'Credit Memo'
                    : '',
              onClose: modalDisclosure.onClose,
            },
            sx: { '.with-header-main': { p: 0, gap: 0 } },
          }}
        >
          <>
            {selectedPostMenu === 'CWT' && (
              <FORoomPostingTaxCreditForm
                handleClose={modalDisclosure.onClose}
                selectedFolioUuid={selectedFolioUuid}
                selectedSubGroup={selectedSubGroup}
                ledgerRefetch={ledgerRefetch}
              />
            )}

            {selectedPostMenu === 'CM' && (
              <FoRoompostingCreditmemoForm
                handleClose={modalDisclosure.onClose}
                creditMemoDetails={{
                  selectedFolioUUID: selectedFolioUuid,
                  guestId: roomPostingLedgerList?.data?.guestID,
                  guestName: roomPostingLedgerList?.data?.guestName,
                  creditAmount: totalPaybles * -1,
                  cutoffDate: moment(currentSalesDate).format('YYYY-MM-DD'),
                  moduleId: 1,
                  subGroupId: selectedSubGroup,
                }}
                handleCheckout={handleApplyCreditMemo}
              />
            )}

            {selectedPostMenu === 'CMU' && (
              <FORoomPostingCreditMemoUsed
                handleClose={modalDisclosure.onClose}
                selectedFolioUuid={selectedFolioUuid}
                selectedSubGroup={selectedSubGroup}
                ledgerRefetch={ledgerRefetch}
              />
            )}
          </>
        </ModalWrapper>
      </>
    </>
  );
};

export default RoomPostingEntry;
